import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AiFillHeart,
  AiOutlineHeart,
  AiOutlineTag,
} from "react-icons/ai";
import { BiCartAdd, BiCartDownload } from "react-icons/bi";
import { FiCopy, FiMail, FiMoreHorizontal, FiShare2 } from "react-icons/fi";
import { FaTelegramPlane, FaWhatsapp } from "react-icons/fa";
import { ProductContext } from "../../Contexts/ProductContext";
import { AuthContext } from "../../Contexts/AuthContext";
import {
  addRemoveProductToCart,
  getSingleProductStockInformation,
  isProductInCart,
} from "../../Utils/cartUtils.jsx";
import {
  addRemoveProductToWishlist,
  isProductInWishlist,
} from "../../Utils/wishlistUtils";
import "./ProductViewStyle.css";
import { isEmpty, printDebugLog, toUpperCase } from "../../Utils/utils.js";
import DynamicTable from "../global/DynamicTable.jsx";
import PriceTag from "../global/PriceTag.jsx";
import { CartOperation, PersonType, SizeType } from "../../Utils/constant.js";
import ImageZoom from "./ImageZoom.jsx";
import { toast } from "react-toastify";
import SafeHtmlContent from "./SafeHtmlContent.jsx";

const ProductView = () => {
  const { productState, productDispatch, getSingleProductDetails, orderState, orderDispatch } =
    useContext(ProductContext);
  const { authState, setLoader } = useContext(AuthContext);
  const [cartBtnDisabled, setCartBtnDisabled] = useState(false);
  const [wishlistBtnDisabled, setWishlistBtnDisabled] = useState(false);
  const [singleProduct, setSingleProduct] = useState({});
  const [isSingleProductLoading, setSingleProductLoading] = useState(true);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareMenuRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isLogged = authState?.mobile?.length > 0;

  useEffect(() => {
    window.scrollTo(0, 0);
    const getSingleProduct = async (inventory, loadInventoryFromApi = false) => {
      setSingleProductLoading(true);
      try {
        const [inventoryDetails, payLoad] = await Promise.all([
          loadInventoryFromApi
            ? getSingleProductStockInformation(null, inventory.prdId)
            : Promise.resolve(inventory),
          getSingleProductDetails(inventory.prdId),
        ]);
        setSingleProduct(inventoryDetails && payLoad ? {
          ...inventoryDetails,
          ...payLoad,
        } : null)
      } catch (err) {
        console.error(`getSingleProduct:${inventory.prdId}`, err);
        setSingleProduct(null);
      } finally {
        setSingleProductLoading(false);
      }
    };
    const sharedProductId = new URLSearchParams(location.search).get("q")?.trim();
    const inventory = sharedProductId
      ? { prdId: sharedProductId }
      : location.state?.inventory;

    printDebugLog("ProductView", "useEffect", inventory);
    if (inventory?.prdId) {
      getSingleProduct(inventory, Boolean(sharedProductId));
    } else {
      setSingleProduct(null);
      setSingleProductLoading(false);
    }
  }, [location.search, location.state, setLoader]);

  useEffect(() => {
    if (!shareMenuOpen) return undefined;

    const closeShareMenu = (event) => {
      if (event.key === "Escape" || !shareMenuRef.current?.contains(event.target)) {
        setShareMenuOpen(false);
      }
    };

    document.addEventListener("pointerdown", closeShareMenu);
    document.addEventListener("keydown", closeShareMenu);
    return () => {
      document.removeEventListener("pointerdown", closeShareMenu);
      document.removeEventListener("keydown", closeShareMenu);
    };
  }, [shareMenuOpen]);

  const getShareDetails = () => {
    const productUrl = new URL("/view/products", window.location.origin);
    productUrl.searchParams.set("q", singleProduct?.prdId || "");
    return {
      title: singleProduct?.name || "Product details",
      text: `View ${singleProduct?.name || "this product"}`,
      url: productUrl.toString(),
    };
  };

  const copyShareLink = async () => {
    const { url } = getShareDetails();
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = url;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        textArea.remove();
      }
      toast.success("Product link copied");
      setShareMenuOpen(false);
    } catch (error) {
      console.error("copyShareLink", error);
      toast.error("Unable to copy product link");
    }
  };

  const openSystemShare = async () => {
    try {
      await navigator.share(getShareDetails());
      setShareMenuOpen(false);
    } catch (error) {
      if (error?.name !== "AbortError") {
        console.error("openSystemShare", error);
        toast.error("Unable to open sharing options");
      }
    }
  };

  const addToCartHandler = (product) => {
    if (isLogged) {
      if (isProductInCart(productState?.cartIds, product?.prdId)) {
        navigate("/cart");
      } else {
        addRemoveProductToCart(
          product,
          productDispatch,
          authState?.mobile,
          CartOperation.add,
          setCartBtnDisabled,
          productState?.cart
          , orderState, orderDispatch
        );
      }
    } else {
      navigate("/login");
    }
  };

  const addToWishlistHandler = (product) => {
    if (isLogged) {
      if (isProductInWishlist(productState?.wishlistIds, product?.prdId)) {
        addRemoveProductToWishlist(
          product,
          productDispatch,
          authState?.mobile,
          false,
          setWishlistBtnDisabled
        );
      } else {
        addRemoveProductToWishlist(
          product,
          productDispatch,
          authState?.mobile,
          true,
          setWishlistBtnDisabled
        );
      }
    } else {
      navigate("/login");
    }
  };

  useEffect(() => {
    if (isSingleProductLoading) {
      setLoader(true);
    } else {
      setLoader(false);
    }
  }, [setLoader, isSingleProductLoading]);

  return (
    <>
      {isSingleProductLoading ? (
        ""
      ) : (
        <>

          {singleProduct ? (
            <div className="single-product-container">
              {
                (Array.isArray(singleProduct?.images) &&
                  singleProduct?.images.length > 0) &&
                <div className="single-product-img">
                  <ImageZoom images={singleProduct?.images} />
                </div>
              }
              <div className="single-product-body">
                <div className="single-product-title-row">
                  <h1>{singleProduct?.name}</h1>
                  <div className="product-share" ref={shareMenuRef}>
                    <button
                      type="button"
                      className="product-share-trigger"
                      aria-label="Share this product"
                      aria-haspopup="menu"
                      aria-expanded={shareMenuOpen}
                      onClick={() => setShareMenuOpen((open) => !open)}
                    >
                      <FiShare2 aria-hidden="true" />
                    </button>

                    {shareMenuOpen && (() => {
                      const shareDetails = getShareDetails();
                      const shareMessage = `${shareDetails.text}\n${shareDetails.url}`;
                      return (
                        <div className="product-share-menu" role="menu" aria-label="Product sharing options">
                          <button type="button" role="menuitem" onClick={copyShareLink}>
                            <FiCopy aria-hidden="true" /> Copy link
                          </button>
                          <a
                            role="menuitem"
                            href={`mailto:?subject=${encodeURIComponent(shareDetails.title)}&body=${encodeURIComponent(shareMessage)}`}
                            onClick={() => setShareMenuOpen(false)}
                          >
                            <FiMail aria-hidden="true" /> Email
                          </a>
                          <a
                            role="menuitem"
                            href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setShareMenuOpen(false)}
                          >
                            <FaWhatsapp aria-hidden="true" /> WhatsApp
                          </a>
                          <a
                            role="menuitem"
                            href={`https://t.me/share/url?url=${encodeURIComponent(shareDetails.url)}&text=${encodeURIComponent(shareDetails.text)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => setShareMenuOpen(false)}
                          >
                            <FaTelegramPlane aria-hidden="true" /> Telegram
                          </a>
                          {typeof navigator.share === "function" && (
                            <button type="button" role="menuitem" onClick={openSystemShare}>
                              <FiMoreHorizontal aria-hidden="true" /> More options
                            </button>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="single-product-price">
                  <PriceTag product={singleProduct} />
                  <span style={{ color: "var(--dark-background-color)", fontWeight: 300, fontSize: "0.7rem" }}>
                    {` (${singleProduct?.quantityShow})`}
                  </span>
                </div>
                <p>
                  <span style={{ color: "var(--dark-background-color)", fontWeight: 300, fontSize: "1rem" }}>Brand:</span>
                  <span style={{ color: "var(--dark-background-color)", fontWeight: 500, fontSize: "0.9rem" }}>{singleProduct?.brand}</span>
                </p>
                <p>
                  <span style={{ color: "var(--dark-background-color)", fontWeight: 300, fontSize: "1rem" }}>Category:</span>
                  <span style={{ color: "var(--dark-background-color)", fontWeight: 500, fontSize: "0.9rem" }}>{singleProduct?.catgName}</span>
                </p>
                <hr className="single-product-body-line" />
                <div className="single-product-benfits">
                  <p>
                    <AiOutlineTag className="tag-icon" /> Fastest Delivery
                  </p>
                  <p>
                    <AiOutlineTag className="tag-icon" /> Inclusive of All Taxes
                  </p>
                </div>

                <div className="single-product-btn">
                  <button
                    className={`single-product-cart-btn pc-center ${cartBtnDisabled && "cart-disabled"
                      }`}
                    onClick={() => addToCartHandler(singleProduct)}
                    disabled={cartBtnDisabled}
                  >
                    {isProductInCart(productState?.cartIds, singleProduct?.prdId) ? (
                      <p className="pc-center">
                        <BiCartDownload className="single-product-btn" /> Go To
                        Cart
                      </p>
                    ) : (
                      <p className="pc-center">
                        <BiCartAdd className="single-product-btn" />
                        Add To Cart
                      </p>
                    )}
                  </button>
                  <button
                    onPointerDown={() => addToWishlistHandler(singleProduct)}
                    className={` single-product-wishlist-btn pc-center ${isProductInWishlist(
                      productState?.wishlistIds,
                      singleProduct?.prdId
                    )
                      ? "wishlist"
                      : ""
                      } ${wishlistBtnDisabled && "wishlist-disabled"}`}
                    disabled={wishlistBtnDisabled}
                  >
                    {isProductInWishlist(
                      productState?.wishlistIds,
                      singleProduct?.prdId
                    ) ? (
                      <p className="pc-center">
                        <AiFillHeart className="single-product-btn" /> Go To
                        Wishlist
                      </p>
                    ) : (
                      <p className="pc-center">
                        <AiOutlineHeart className="single-product-btn" />
                        Add To Wishlist
                      </p>
                    )}
                  </button>
                </div>
                <div className="single-product-description">
                  <p style={{ fontSize: "1.4rem", color: "var(--dark-background-color)" }}>Specification:</p>
                  <DynamicTable specs={[
                    { header: "Gender", value: `${PersonType.None === toUpperCase(singleProduct.personTypeStr) ? "Everyone" : singleProduct.personTypeStr}` },
                    { header: "Size", value: `${SizeType.None == toUpperCase(singleProduct.sizeTypeStr) ? "All" : singleProduct.sizeTypeStr}` },
                    { header: "Serial No", value: `${singleProduct.hsnCode}` },
                    { header: "Movement", value: `${singleProduct.movementType}` },
                    { header: "Condition", value: `${singleProduct.condition}` },
                    { header: "Material", value: `${singleProduct.caseMetal}` },
                    { header: "Crystal", value: `${singleProduct.jewels}` },
                    { header: "Case Size", value: `${singleProduct.caseSize}` },
                    { header: "Lug Width", value: `${singleProduct.lugWidth}` },
                    { header: "Strap Color", value: `${singleProduct.strapColorDetails}`, code: `${singleProduct.strapColorCode}` },
                    { header: "Dial Color", value: `${singleProduct.dialColorDetails}`, code: `${singleProduct.dialColoCode}` },
                    { header: "Water Resistance", value: `${singleProduct.waterResistance ? "Yes" : "No"}` }
                  ]} />
                </div>
                <div className="single-product-description">
                  <p style={{ fontSize: "1.4rem", color: "var(--dark-background-color)" }}>About Item:</p>
                  {!isEmpty(singleProduct?.subCatgName) && (
                    <SafeHtmlContent content={singleProduct?.subCatgName} title="Product category details" />
                  )}

                  {!isEmpty(singleProduct?.aboutItem) && (
                    <SafeHtmlContent content={singleProduct?.aboutItem} />
                  )}

                  {!isEmpty(singleProduct?.tags) && (
                    <SafeHtmlContent content={singleProduct?.tags} title="Product tags" />
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p style={{ marginTop: "3.6rem" }}>Product Undefined</p>
          )}
        </>
      )}
    </>
  );
};
export { ProductView };
